import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UsersIcon, CheckIcon, XIcon, AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Invite Acceptance Component - handles accepting band invitations via token
 */
const InviteAcceptance = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch invite information
  useEffect(() => {
    const fetchInviteInfo = async () => {
      try {
        const response = await fetch(`/api/bands/invites/${token}/info`);
        
        if (response.ok) {
          const result = await response.json();
          setInviteInfo(result.data);
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Invite not found');
        }
      } catch (error) {
        console.error('Error fetching invite info:', error);
        setError('Failed to load invite information');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInviteInfo();
    } else {
      setError('Invalid invite link');
      setLoading(false);
    }
  }, [token]);

  // Accept the invitation
  const handleAcceptInvite = async () => {
    if (!isAuthenticated) {
      alert('You must be logged in to accept this invitation. Please sign in and try again.');
      return;
    }

    setAccepting(true);
    try {
      const response = await fetch(`/api/bands/invites/${token}/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSuccess(true);
        setTimeout(() => {
          navigate('/bands'); // Redirect to band management page
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Error accepting invite:', error);
      setError('Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <XIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to the Band!</h1>
          <p className="text-gray-600 mb-6">
            You've successfully joined <strong>{inviteInfo?.band?.name}</strong> with {inviteInfo?.role} permissions.
          </p>
          <p className="text-sm text-gray-500">Redirecting to your bands...</p>
        </div>
      </div>
    );
  }

  if (!inviteInfo?.is_valid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircleIcon className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invitation Expired</h1>
          <p className="text-gray-600 mb-6">
            This invitation link has expired or is no longer valid. Please ask the band admin for a new invitation.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          <UsersIcon className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Band Invitation</h1>
          <p className="text-gray-600">You've been invited to join a band!</p>
        </div>

        {/* Band Information */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{inviteInfo.band.name}</h2>
          {inviteInfo.band.description && (
            <p className="text-gray-600 mb-3">{inviteInfo.band.description}</p>
          )}
          
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Your role:</span>
              <span className="font-medium capitalize">{inviteInfo.role}</span>
            </div>
            <div className="flex justify-between">
              <span>Invited by:</span>
              <span className="font-medium">{inviteInfo.created_by?.username || 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span>Expires:</span>
              <span className="font-medium">{formatDate(inviteInfo.expires_at)}</span>
            </div>
          </div>
        </div>

        {/* Role Information */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">As a {inviteInfo.role}, you can:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            {inviteInfo.role === 'member' && (
              <>
                <li>• View and edit band sheets</li>
                <li>• Create and manage versions</li>
                <li>• View band setlists</li>
                <li>• Fork and export content</li>
              </>
            )}
            {inviteInfo.role === 'admin' && (
              <>
                <li>• Full access to all band features</li>
                <li>• Manage band settings and visibility</li>
                <li>• Invite and remove members</li>
                <li>• Delete the band</li>
              </>
            )}
          </ul>
        </div>

        {/* Authentication Notice */}
        {!isAuthenticated && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertCircleIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
              <div>
                <p className="text-sm text-yellow-800">
                  You need to be logged in to accept this invitation. Please sign in to continue.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {isAuthenticated ? (
            <>
              <button
                onClick={handleAcceptInvite}
                disabled={accepting}
                className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                  accepting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {accepting ? 'Accepting...' : 'Accept Invitation'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  // Store the invite token in localStorage to return after login
                  localStorage.setItem('pendingInviteToken', token);
                  navigate(`/?login=true`);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Sign In to Accept
              </button>
              
              <button
                onClick={() => {
                  // Store the invite token in localStorage to return after signup
                  localStorage.setItem('pendingInviteToken', token);
                  navigate(`/?signup=true`);
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Sign Up to Accept
              </button>
            </>
          )}
          
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Decline
          </button>
        </div>

        {/* User Info */}
        {isAuthenticated && user && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Joining as: <span className="font-medium">{user.username}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteAcceptance;